/**
 * The node-type registry — every node type the editor and executor know, the
 * categories they group into, and the capability lists that say which of them
 * may be masked, deleted, duplicated or replaced.
 */

/* Node type enum for type-safe access
 * Usage: NodeType.webhookTrigger, NodeType.aiTask, etc.
 */
export const NodeType = {
  agentInputTrigger: "agentInputTrigger",
  aiPassiveSatisfactionScore: "aiPassiveSatisfactionScore",
  aiTask: "aiTask",
  branching: "branching",
  browserbase: "browserbase",
  cueNode: "cueNode",
  customScript: "customScript",
  doWhile: "doWhile",
  doWhileStart: "doWhileStart",
  exaai: "exaai",
  fileExtraction: "fileExtraction",
  pdfSplit: "pdfSplit",
  firecrawl: "firecrawl",
  humanInput: "humanInput",
  jsonata: "jsonata",
  jsonManipulation: "jsonManipulation",
  jsonParse: "jsonParse",
  listConcatenation: "listConcatenation",
  loop: "loop",
  loopStart: "loopStart",
  manualTrigger: "manualTrigger",
  newsMonitorTrigger: "newsMonitorTrigger",
  outputNode: "outputNode",
  parseSatisfactionReply: "parseSatisfactionReply",
  platformListenerTrigger: "platformListenerTrigger",
  parallelai: "parallelai",
  plugin: "plugin",
  pluginTrigger: "pluginTrigger",
  recordScore: "recordScore",
  scheduleTrigger: "scheduleTrigger",
  searchCollection: "searchCollection",
  selectTrigger: "selectTrigger",
  makePhoneCall: "makePhoneCall",
  nexusApi: "nexusApi",
  sendMessage: "sendMessage",
  sendSatisfactionPrompt: "sendSatisfactionPrompt",
  sixtyfour: "sixtyfour",
  smartAction: "smartAction",
  surfer: "surfer",
  textFormatter: "textFormatter",
  timer: "timer",
  webhookTrigger: "webhookTrigger",
  workflow: "workflow"
} as const;

/**
 * Type derived from NodeType constant
 */
export type NodeTypeEnum = (typeof NodeType)[keyof typeof NodeType];
/**
 * All available node types as a const array
 * Derived from NodeType constant for single source of truth
 * Useful for validation and iteration
 */
export const NODE_TYPES = Object.values(NodeType);

/**
 * Node categories for organization and filtering
 */
export const NODE_CATEGORIES = {
  TRIGGER: [
    NodeType.agentInputTrigger,
    NodeType.webhookTrigger,
    NodeType.scheduleTrigger,
    NodeType.pluginTrigger,
    NodeType.newsMonitorTrigger,
    NodeType.platformListenerTrigger,
    NodeType.selectTrigger,
    NodeType.manualTrigger
  ],
  AI: [
    NodeType.aiTask,
    NodeType.parallelai,
    NodeType.cueNode,
    NodeType.aiPassiveSatisfactionScore,
    NodeType.parseSatisfactionReply
  ],
  DATA: [
    NodeType.searchCollection,
    NodeType.sixtyfour,
    NodeType.exaai,
    NodeType.fileExtraction,
    NodeType.pdfSplit
  ],
  INTEGRATION: [NodeType.plugin, NodeType.firecrawl, NodeType.smartAction, NodeType.nexusApi],
  CONTROL: [
    NodeType.branching,
    NodeType.doWhile,
    NodeType.doWhileStart,
    NodeType.loop,
    NodeType.loopStart,
    NodeType.workflow
  ],
  IO: [
    NodeType.humanInput,
    NodeType.sendMessage,
    NodeType.sendSatisfactionPrompt,
    NodeType.makePhoneCall,
    NodeType.outputNode
  ],
  UTILITY: [
    NodeType.customScript,
    NodeType.textFormatter,
    NodeType.jsonManipulation,
    NodeType.jsonParse,
    NodeType.jsonata,
    NodeType.listConcatenation,
    NodeType.timer,
    NodeType.recordScore
  ],
  NEXUS: [NodeType.nexusApi]
} as const;

/**
 * Node types that cannot be masked (skipped) during execution.
 * - Triggers: Must run to start workflow
 * - Loops: Control flow structure
 * - Branching: Decision points that control flow
 * - Output: Terminal node that must run
 */
export const NON_MASKABLE_NODE_TYPES = [
  ...NODE_CATEGORIES.TRIGGER,
  NodeType.doWhile,
  NodeType.doWhileStart,
  NodeType.loop,
  NodeType.loopStart,
  NodeType.branching,
  NodeType.outputNode
] as const;

/**
 * Node types that cannot be individually deleted via menu or toolbar.
 * These nodes can still be cascade-deleted when their parent container is deleted.
 * - LoopStart: Structural child of loop, deleting it orphans the loop
 */
export const NON_DELETABLE_NODE_TYPES = [NodeType.doWhileStart, NodeType.loopStart] as const;

/**
 * Node types that cannot be individually duplicated via menu or toolbar.
 * These nodes CAN be duplicated when their parent container is also selected.
 * - LoopStart: Structural child of loop — only duplicated alongside its parent loop
 */
export const NON_DUPLICATABLE_NODE_TYPES = [NodeType.doWhileStart, NodeType.loopStart] as const;

/**
 * Node types that cannot be replaced (changed to a different type).
 * - Triggers: Define workflow entry points
 * - Loop controls: Structural nodes with special behavior
 * - Branching: Decision points that control flow
 * - Output: Terminal node with specific purpose
 */
export const NON_REPLACEABLE_NODE_TYPES = [
  ...NODE_CATEGORIES.TRIGGER,
  NodeType.doWhile,
  NodeType.doWhileStart,
  NodeType.loop,
  NodeType.loopStart,
  NodeType.branching,
  NodeType.outputNode
] as const;

/**
 * Node types that cannot be replacement targets.
 * These types cannot be selected when replacing another node.
 */
export const NON_REPLACEMENT_TARGET_TYPES = [
  ...NODE_CATEGORIES.TRIGGER,
  NodeType.doWhile,
  NodeType.doWhileStart,
  NodeType.loop,
  NodeType.loopStart,
  NodeType.selectTrigger
] as const;
